import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';
import { GoogleGenAI } from "@google/genai";


const Chatbot = ({ isOpen, onClose, inline = false, headerColor }) => {
  // Initialize AI only if API key is available
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;
 
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi there! 👋 I'm CARAI, your AI assistant from CodeAlignR. I'm here to help you discover how our AI solutions can transform your business! 😊\n\nChat with CARAI - I can help you with:\n• 🤖 AI Voice Agents\n• 💬 AI Chatbots\n• 💰 Pricing & ROI\n• 🚀 Getting Started",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickReplies = [
    "Learn More",
    "Pricing",
    "Free Consultation",
    "Get Started",
    "Contact Us",
    "Book Demo"
  ];

  // Navigation functions
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToContact = () => {
    scrollToSection('contact');
  };

  const navigateToServices = () => {
    scrollToSection('services');
  };

  const navigateToComparison = () => {
    scrollToSection('comparison');
  };

  const navigateToWhyChooseUs = () => {
    scrollToSection('why-choose-us');
  };

  const navigateToWorkflow = () => {
    scrollToSection('workflow');
  };

  // Handle navigation based on bot response
  const handleNavigation = (response) => {
    const lowerResponse = response.toLowerCase();
    
    if (lowerResponse.includes('services section') || lowerResponse.includes('services')) {
      setTimeout(() => navigateToServices(), 1000);
    } else if (lowerResponse.includes('comparison section') || lowerResponse.includes('comparison')) {
      setTimeout(() => navigateToComparison(), 1000);
    } else if (lowerResponse.includes('why choose us') || lowerResponse.includes('about') || lowerResponse.includes('benefits')) {
      setTimeout(() => navigateToWhyChooseUs(), 1000);
    } else if (lowerResponse.includes('workflow') || lowerResponse.includes('process')) {
      setTimeout(() => navigateToWorkflow(), 1000);
    } else if (lowerResponse.includes('contact form') || lowerResponse.includes('contact us')) {
      setTimeout(() => navigateToContact(), 1000);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chatbot opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    // Focus input after sending message
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);


   

    try {
      // Check if AI is available
      if (!ai) {
        throw new Error('API key not configured');
      }

      // Call Gemini API with correct method from official docs
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: `You are CARAI, a friendly AI assistant for CodeAlignR. Your job is to help users navigate the website and direct them to the contact form.

CRITICAL RULES:
- MAXIMUM 2 sentences per response
- NO bullet points or lists
- NO structured formatting
- Use natural, casual language
- Be enthusiastic but brief
- ALWAYS end by directing them to contact form OR suggesting a website section

NAVIGATION CAPABILITIES:
You can direct users to these website sections:
- "services" - AI Voice Agents and Chatbots details
- "comparison" - AI vs Traditional methods comparison
- "why-choose-us" - Benefits and advantages
- "workflow" - How the process works
- "contact" - Contact form

RESPONSE STRATEGY:
1. Acknowledge their question briefly
2. Suggest relevant website section OR direct to contact form
3. Be friendly and helpful
4. Use emojis naturally

COMPANY OVERVIEW:
CodeAlignR specializes in AI Voice Agents and Chatbots for business automation. We help businesses never miss calls, save over 50% on costs, and get set up in days.

RESPONSE EXAMPLES:
❌ WRONG: "Here are the key benefits: ✅ 24/7 availability ✅ Cost savings..."
✅ RIGHT: "Great question! Check out our services section to learn more about our AI solutions. Then fill out our contact form to speak with a representative! 😊"

NAVIGATION EXAMPLES:
- For pricing questions: "I'd love to discuss pricing with you! Please fill out our contact form and we'll get back to you with a personalized quote! 💰"
- For service details: "Check out our services section to see all our AI solutions! Then contact us for more details! 🚀"
- For comparisons: "Our comparison section shows exactly how we're better than traditional methods! Take a look and then reach out to us! 📊"

ALWAYS END WITH:
- "Please fill out our contact form and one of our representatives will get back to you!"
- "Check out our [section] and then contact us!"
- "Take a look at our [section] and reach out to us!"

CONVERSATION HISTORY:
${messages.map(msg => `${msg.sender === 'user' ? 'User' : 'CARAI'}: ${msg.text}`).join('\n')}

User: ${inputMessage}

CARAI:`
      });

      const botResponse = response.text;
      
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Check if response suggests navigation and handle it
      handleNavigation(botResponse);
    } catch (error) {
      console.error('Error sending message:', error);
      let errorText = "I'm sorry, I'm having trouble connecting right now.";
      
      if (error.message === 'API key not configured') {
        errorText = "Hi! I'm CARAI, but I need to be configured first. Please contact our team to get me up and running! 😊";
      }
      
      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Focus input after bot responds
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickReply = (reply) => {
    setInputMessage(reply);
    // Auto-send the quick reply
    setTimeout(() => {
      const userMessage = {
        id: messages.length + 1,
        text: reply,
        sender: 'user',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
      setIsLoading(true);
      
      // Send the message
      sendQuickReply(reply);
    }, 100);
  };

  const sendQuickReply = async (message) => {
    // Check if AI is available first
    if (!ai) {
      const errorMessage = {
        id: messages.length + 2,
        text: "Hi! I'm CARAI, but I need to be configured first. Please contact our team to get me up and running! 😊",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    try {
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: `You are CARAI, a friendly AI assistant for CodeAlignR. Your job is to help users navigate the website and direct them to the contact form.

CRITICAL RULES:
- MAXIMUM 2 sentences per response
- NO bullet points or lists
- NO structured formatting
- Use natural, casual language
- Be enthusiastic but brief
- ALWAYS end by directing them to contact form OR suggesting a website section

NAVIGATION CAPABILITIES:
You can direct users to these website sections:
- "services" - AI Voice Agents and Chatbots details
- "comparison" - AI vs Traditional methods comparison
- "why-choose-us" - Benefits and advantages
- "workflow" - How the process works
- "contact" - Contact form

RESPONSE STRATEGY:
1. Acknowledge their question briefly
2. Suggest relevant website section OR direct to contact form
3. Be friendly and helpful
4. Use emojis naturally

COMPANY OVERVIEW:
CodeAlignR specializes in AI Voice Agents and Chatbots for business automation. We help businesses never miss calls, save over 50% on costs, and get set up in days.

RESPONSE EXAMPLES:
❌ WRONG: "Here are the key benefits: ✅ 24/7 availability ✅ Cost savings..."
✅ RIGHT: "Great question! Check out our services section to learn more about our AI solutions. Then fill out our contact form to speak with a representative! 😊"

NAVIGATION EXAMPLES:
- For pricing questions: "I'd love to discuss pricing with you! Please fill out our contact form and we'll get back to you with a personalized quote! 💰"
- For service details: "Check out our services section to see all our AI solutions! Then contact us for more details! 🚀"
- For comparisons: "Our comparison section shows exactly how we're better than traditional methods! Take a look and then reach out to us! 📊"

ALWAYS END WITH:
- "Please fill out our contact form and one of our representatives will get back to you!"
- "Check out our [section] and then contact us!"
- "Take a look at our [section] and reach out to us!"

CONVERSATION HISTORY:
${messages.map(msg => `${msg.sender === 'user' ? 'User' : 'CARAI'}: ${msg.text}`).join('\n')}

User: ${message}

CARAI:`
      });

      const botResponse = response.text;
      
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Check if response suggests navigation and handle it
      handleNavigation(botResponse);
    } catch (error) {
      console.error('Error sending message:', error);
      let errorText = "Oops! 😅 I'm having a little trouble right now. Could you try asking me again?";
      
      if (error.message === 'API key not configured') {
        errorText = "Hi! I'm CARAI, but I need to be configured first. Please contact our team to get me up and running! 😊";
      }
      
      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Focus input after quick reply bot responds
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-side-panel">
      <div className="chatbot-container">
        <div 
          className="chatbot-header"
          style={inline && headerColor ? { 
            background: `linear-gradient(135deg, ${headerColor} 0%, ${headerColor}dd 100%)` 
          } : undefined}
        >
          <div className="chatbot-title">
            <i className="fas fa-robot"></i>
            <span>CARAI</span>
          </div>
          {!inline && (
            <button className="close-button" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className="chatbot-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
            >
              <div className="message-content">
                {message.text}
              </div>
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Reply Buttons */}
        {messages.length <= 2 && (
          <div className="quick-replies">
            <div className="quick-replies-label">Quick questions:</div>
            <div className="quick-replies-buttons">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  className="quick-reply-btn"
                  onClick={() => handleQuickReply(reply)}
                  disabled={isLoading}
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="chatbot-input">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message here..."
            disabled={isLoading}
            rows="1"
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="send-button"
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
